/**
 * useAuthToken Hook
 * 
 * Custom hook for acquiring access tokens for API calls
 * Handles silent token acquisition and fallback to interactive flow
 */

import { useState, useCallback } from 'react';
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { apiRequest } from "./authConfig";

/**
 * Hook for acquiring and managing access tokens
 * 
 * @returns {Object} - { token, getToken, isLoading, error }
 */
const useAuthToken = () => {
    const { instance, accounts } = useMsal();
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Get access token for API calls
     * Attempts silent acquisition first, falls back to redirect if needed
     */
    const getToken = useCallback(async () => {
        if (accounts.length === 0) {
            console.error('[useAuthToken] No accounts available');
            setError('No authenticated account found');
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Attempt silent token acquisition
            const request = {
                ...apiRequest,
                account: accounts[0],
            };

            console.log('[useAuthToken] Attempting silent token acquisition for scopes:', request.scopes);

            const response = await instance.acquireTokenSilent(request);

            console.log('[useAuthToken] Token acquired silently:', {
                expiresOn: response.expiresOn,
                scopes: response.scopes,
                tokenType: response.tokenType,
            });

            setToken(response.accessToken);
            setIsLoading(false);
            return response.accessToken;

        } catch (err) {
            // Handle interaction required errors
            if (err instanceof InteractionRequiredAuthError) {
                console.warn('[useAuthToken] Interaction required, attempting popup');

                try {
                    // Use popup instead of redirect (redirect reloads the page)
                    const popupResponse = await instance.acquireTokenPopup({
                        ...apiRequest,
                        account: accounts[0],
                    });

                    if (popupResponse?.accessToken) {
                        console.log('[useAuthToken] Token acquired via popup');
                        setToken(popupResponse.accessToken);
                        setIsLoading(false);
                        return popupResponse.accessToken;
                    }
                    return null;

                } catch (popupError) {
                    console.error('[useAuthToken] Popup token acquisition failed:', popupError);
                    setError('Failed to acquire token. Please sign in again.');
                    setIsLoading(false);
                    return null;
                }
            }

            // Handle other errors
            console.error('[useAuthToken] Token acquisition failed:', err);
            setError(err.message || 'Failed to acquire access token');
            setIsLoading(false);
            return null;
        }
    }, [instance, accounts]);

    /**
     * Clear stored token (useful for forcing refresh)
     */
    const clearToken = useCallback(() => {
        setToken(null);
        setError(null);
    }, []);

    return {
        token,
        getToken,
        clearToken,
        isLoading,
        error,
    };
};

export default useAuthToken;
